import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

import { logger } from '@service-initiation/shared-clients-osp-logger';
import { DeviceId } from '@service-initiation/shared-clients-tech-care-services';
import { storage } from '@service-initiation/shared-utils-jotai';
import {
  getDeviceColorOptions,
  getDeviceDetails,
} from '@service-initiation/tech-care-data-access-tech-care-services';

import { type DeviceDetails } from '../types';
import { deviceColorOptionIdsAtom } from './deviceColorOptionIdsAtom';
import { deviceTypeAtom } from './selectedDeviceTypeAsyncAtom';

const getDeviceDetailsWithErrorHandling = async (deviceTypeId: number) => {
  try {
    return await getDeviceDetails(deviceTypeId);
  } catch (e) {
    const err = e as Error;
    err.message = 'Error occurred while trying to hydrate device details';
    logger.error(err, {
      data: { deviceTypeId },
    });
  }
};

const getDeviceColorOptionsWithErrorHandling = async (
  device: DeviceDetails
) => {
  try {
    if (device.isEligibleForColorOptions) {
      return await getDeviceColorOptions(device);
    }
  } catch (e) {
    const err = e as Error;
    err.message = 'Error occurred while trying to hydrate device color options';
    logger.error(err, {
      data: { device },
    });
  }
};

export const deviceDetailsAtom = atomWithStorage<DeviceDetails | undefined>(
  'si-tec-car-device-details',
  undefined,
  storage<DeviceDetails | undefined>(),
  {
    getOnInit: true,
  }
);

export const deviceDetailsAsyncAtom = atom(async (get) => {
  const deviceType = get(deviceTypeAtom);
  const deviceDetails = get(deviceDetailsAtom);
  const deviceColorOptionIds = get(deviceColorOptionIdsAtom);
  const selectedDevice = deviceDetails?.id || deviceType?.id;
  const isOtherDeviceWithParentId =
    deviceDetails?.id === DeviceId.OtherChild &&
    deviceDetails?.parent?.id !== DeviceId.Other;
  if (selectedDevice && !isOtherDeviceWithParentId) {
    const updatedDeviceDetails = selectedDevice
      ? await getDeviceDetailsWithErrorHandling(selectedDevice)
      : undefined;
    const colorOptions = updatedDeviceDetails
      ? await getDeviceColorOptionsWithErrorHandling(updatedDeviceDetails)
      : undefined;

    const selectedDeviceColorOption = colorOptions?.find((option) =>
      option.option_ids.every((id) => deviceColorOptionIds?.includes(id))
    );
    return updatedDeviceDetails
      ? ({
          ...updatedDeviceDetails,
          colorOptions,
          selectedDeviceColorOption,
        } as DeviceDetails)
      : undefined;
  } else {
    return deviceDetails;
  }
});
